import { lumi } from '@/lib/lumi'

export type AssignmentKind = 'quiz' | 'assignment'

export interface QuizQuestion {
  question: string
  options: string[]
  correctIndex: number
}

export interface CourseAssignment {
  _id: string
  courseId: string
  courseSlug: string
  moduleId?: string
  kind: AssignmentKind
  title: string
  description?: string
  fileUrl?: string
  fileName?: string
  externalUrl?: string
  dueLabel?: string
  questions?: QuizQuestion[]
  creator?: string
  createdAt: string
  updatedAt: string
}

function nowIso() {
  return new Date().toISOString()
}

export function kindLabel(kind: AssignmentKind | undefined) {
  return kind === 'quiz' ? 'Quiz' : 'Assignment'
}

export async function fetchCourseAssignments(courseId: string) {
  const { list } = await lumi.entities.course_assignments.list({
    filter: { courseId },
    sort: { createdAt: -1 },
    limit: 200,
  })
  return (list ?? []) as CourseAssignment[]
}

export async function fetchAllAssignments() {
  const { list } = await lumi.entities.course_assignments.list({
    sort: { createdAt: -1 },
    limit: 400,
  })
  return (list ?? []) as CourseAssignment[]
}

export async function uploadAssignmentFile(file: File) {
  const results = await lumi.tools.file.upload([file])
  const first = results?.[0]
  if (!first?.fileUrl) {
    throw new Error(first?.uploadError || 'No se pudo subir el archivo.')
  }
  return { url: first.fileUrl, name: first.fileName || file.name }
}

export async function createCourseAssignment(payload: {
  courseId: string
  courseSlug: string
  moduleId: string
  kind: AssignmentKind
  title: string
  description: string
  fileUrl: string
  fileName: string
  externalUrl: string
  dueLabel: string
  questions?: QuizQuestion[]
  creator: string
}) {
  const stamp = nowIso()
  return (await lumi.entities.course_assignments.create({
    courseId: payload.courseId,
    courseSlug: payload.courseSlug,
    moduleId: payload.moduleId,
    kind: payload.kind,
    title: payload.title.trim(),
    description: payload.description.trim(),
    fileUrl: payload.fileUrl,
    fileName: payload.fileName,
    externalUrl: payload.externalUrl.trim(),
    dueLabel: payload.dueLabel.trim(),
    questions: payload.kind === 'quiz' ? payload.questions ?? [] : [],
    creator: payload.creator,
    createdAt: stamp,
    updatedAt: stamp,
  })) as CourseAssignment
}

export async function updateCourseAssignment(
  id: string,
  payload: {
    title: string
    description: string
    dueLabel: string
    externalUrl: string
    questions: QuizQuestion[]
  },
) {
  if (typeof id !== 'string' || id === '[object Object]') {
    throw new Error('ID inválido')
  }
  return (await lumi.entities.course_assignments.update(id, {
    title: payload.title.trim(),
    description: payload.description.trim(),
    dueLabel: payload.dueLabel.trim(),
    externalUrl: payload.externalUrl.trim(),
    questions: payload.questions,
    updatedAt: nowIso(),
  })) as CourseAssignment
}

export async function deleteCourseAssignment(id: string) {
  if (typeof id !== 'string' || id === '[object Object]') {
    throw new Error('ID inválido')
  }
  await lumi.entities.course_assignments.delete(id)
}
